import React, { createContext, useContext, useEffect, useReducer } from 'react'

import { INGREDIENTS_TREE_RDB } from '__constants__'
import PropTypes from 'prop-types'
import { transformSelectedTreeLevel } from 'domains/IngredientsTree/helpers'
import { useGetIngredientsTree } from 'domains/IngredientsTree/hooks'

const initialState = {
  ingredientsTree: null,
  currentTreeLevel: [],
  selectedNode: null,
  isLoading: false,
  optimisticTree: null // Temporary tree state for optimistic updates
}

function ingredientReducer(state, action) {
  switch (action.type) {
    case 'SET_CURRENT_TREE_LEVEL':
      return { ...state, currentTreeLevel: action.payload }
    case 'SET_SELECTED_NODE':
      return { ...state, selectedNode: action.payload }
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload }
    case 'SET_OPTIMISTIC_TREE':
      return { ...state, optimisticTree: action.payload }
    case 'CLEAR_OPTIMISTIC_TREE':
      return { ...state, optimisticTree: null }
    default:
      throw new Error(`Unhandled action type: ${action.type}`)
  }
}

const IngredientContext = createContext()

export const useIngredientContext = () => useContext(IngredientContext)

const IngredientProvider = ({ children }) => {
  const {
    data: ingredientsTree,
    error: ingredientsTreeError,
    loading: ingredientsTreeLoading,
    retry: retryIngredientsTree
  } = useGetIngredientsTree()
  const [state, dispatch] = useReducer(ingredientReducer, initialState)

  // Use optimistic tree if available, otherwise use real tree
  const displayTree = state.optimisticTree || ingredientsTree

  useEffect(() => {
    if (displayTree) {
      const selectedPath = state?.selectedNode?._path ?? INGREDIENTS_TREE_RDB
      const result = transformSelectedTreeLevel(displayTree, selectedPath)

      dispatch({ type: 'SET_CURRENT_TREE_LEVEL', payload: result })
      if (result?.length && !state?.selectedNode) {
        dispatch({ type: 'SET_SELECTED_NODE', payload: result?.[0] })
      } else if (state?.selectedNode) {
        const selectedNode = result.find(
          (node) => node._id === state?.selectedNode?._id
        )
        if (selectedNode) {
          dispatch({ type: 'SET_SELECTED_NODE', payload: selectedNode })
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayTree, state?.selectedNode?._path])

  // Clear optimistic tree when real tree updates (after successful save)
  useEffect(() => {
    if (ingredientsTree && state.optimisticTree) {
      // Real tree has been updated, clear optimistic state
      dispatch({ type: 'CLEAR_OPTIMISTIC_TREE' })
    }
  }, [ingredientsTree])

  return (
    <IngredientContext.Provider
      value={{
        ...state,
        ingredientsTree,
        ingredientsTreeError,
        ingredientsTreeLoading,
        retryIngredientsTree,
        displayTree, // Tree to display (optimistic or real)
        dispatch
      }}>
      {children}
    </IngredientContext.Provider>
  )
}

IngredientProvider.propTypes = {
  children: PropTypes.node.isRequired
}

export default IngredientProvider
